import React, { useEffect, useState } from 'react';
import type { AppContext } from '../app';

interface Props {
  ctx: AppContext;
  onLock: () => void | Promise<void>;
}

export function AmbientRail({ ctx, onLock }: Props): JSX.Element {
  const [msUntilLock, setMsUntilLock] = useState<number | null>(null);
  const [peers, setPeers] = useState<number>(0);
  const [openIncidents, setOpenIncidents] = useState<number>(0);
  const [onShiftCount, setOnShiftCount] = useState<number>(0);
  /**
   * U1.3c — Mobile-fit: collapse the 5-chip ambient rail to a single
   * compact pill once the user has scrolled past the first viewport
   * height. Expand on tap. Recovers ~80-120px above the fold on mobile.
   * Desktop ignores this collapsed state (CSS guard).
   */
  const [collapsed, setCollapsed] = useState<boolean>(false);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      // 120px scroll threshold — well past header but before content.
      setCollapsed(y > 120);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tickKeyring = async () => {
      const s = await ctx.keyring.status();
      if (cancelled) return;
      if (s.kind === 'status' && s.unlocked) {
        setMsUntilLock(s.msUntilLock);
      }
    };
    const tickMesh = async () => {
      try {
        const stats = await ctx.meshNode.stats();
        if (cancelled) return;
        setPeers(stats.connected);
      } catch {
        // mesh not started or torn down; leave peers at last known value
      }
    };
    const tickAlds = async () => {
      try {
        const db = ctx.storage.db;
        const [open, onShift] = await Promise.all([
          db.incident_meta
            .where('status').anyOf('open', 'triaged', 'dispatched', 'on-scene').count(),
          db.roster_status.where('onShift').equals(1).count(),
        ]);
        if (cancelled) return;
        setOpenIncidents(open);
        setOnShiftCount(onShift);
      } catch {
        // storage may be locked between transitions; leave at last values
      }
    };
    void tickKeyring();
    void tickMesh();
    void tickAlds();
    const interval = setInterval(() => {
      void tickKeyring();
      void tickMesh();
      void tickAlds();
    }, 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [ctx]);

  /**
   * U3 — humanize the auto-lock timer. Previously "14M 58S" which
   * reads as engineer-speak; now "~15 min" / "under a min" / "now" for
   * the common cases. Sub-second precision isn't useful to the end
   * user; if they care they can hover the chip for the title attr.
   */
  const formatLockTime = (ms: number | null) => {
    if (ms === null) return '—';
    if (!Number.isFinite(ms)) return 'never';
    const s = Math.ceil(ms / 1000);
    if (s <= 0) return 'now';
    if (s < 30) return 'under a min';
    if (s < 90) return '~1 min';
    const m = Math.round(s / 60);
    return `~${m} min`;
  };
  /** Precise format reserved for the title= tooltip on the chip. */
  const formatLockTimeExact = (ms: number | null) => {
    if (ms === null || !Number.isFinite(ms)) return '';
    const s = Math.ceil(ms / 1000);
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  if (collapsed) {
    // Single-chip collapsed view. Tap to expand (resets scroll to top
    // so the user can see the full rail without ambiguity).
    return (
      <div
        className="ambient-rail ambient-rail-collapsed"
        role="button"
        tabIndex={0}
        onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        aria-label="Expand ambient rail"
      >
        <style>{styles}</style>
        <span className="ambient-chip ambient-summary">
          <span className="pulse-dot" />
          {peers} peer{peers === 1 ? '' : 's'} · {openIncidents} open · {onShiftCount} on shift · locks {formatLockTime(msUntilLock)}
        </span>
        <button className="ambient-lock" onClick={(e) => { e.stopPropagation(); void onLock(); }}>
          lock ↓
        </button>
      </div>
    );
  }

  return (
    <div className="ambient-rail">
      <style>{styles}</style>
      <span className="ambient-label">ambient</span>
      <span className="ambient-chip">
        <span className="pulse-dot" />
        mesh · {peers} peer{peers === 1 ? '' : 's'}
      </span>
      <span className="ambient-chip">
        🔔 {openIncidents} open
      </span>
      <span className="ambient-chip">
        ◉ {onShiftCount} on shift
      </span>
      <span style={{ flex: 1 }} />
      <span className="ambient-chip lock-chip" title={`auto-lock in ${formatLockTimeExact(msUntilLock)}`}>
        ⌬ locks in {formatLockTime(msUntilLock)}
      </span>
      <button className="ambient-lock" onClick={() => void onLock()}>
        lock ↓
      </button>
    </div>
  );
}

const styles = `
.ambient-rail {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  padding: 10px 14px;
  background: rgba(17, 22, 26, 0.4);
  border: 1px solid var(--rule);
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--ink-dim);
  align-items: center;
}
@media (max-width: 600px) {
  .ambient-rail { gap: 6px 10px; padding: 8px 10px; }
  .ambient-rail span[style*="flex"] { display: none; }  /* spacer collapses on mobile */
}
.ambient-label { color: var(--phos); margin-right: 4px; }
.ambient-chip {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid var(--rule);
}
.lock-chip { border-color: var(--rule-bright); color: var(--ink); }
.ambient-lock {
  padding: 4px 12px;
  border: 1px solid var(--rule-bright);
  color: var(--ink-dim);
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}
.ambient-lock:hover { border-color: var(--amber); color: var(--amber); }

/* U1.3c collapsed-rail styles. Single chip on the left, lock button
 * on the right. Tapping the chip (anywhere outside the lock button)
 * scrolls to top to expand. */
.ambient-rail-collapsed {
  cursor: pointer;
  padding: 6px 10px;
  gap: 6px;
  flex-wrap: nowrap;
  justify-content: space-between;
}
.ambient-rail-collapsed:hover { background: rgba(17, 22, 26, 0.6); }
.ambient-summary {
  font-size: 9px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
`;
